(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-prism-orbit]';
  var DRAG_THRESHOLD = 12;
  var SNAP_DISTANCE = 52;
  var SNAP_VELOCITY = 0.34;
  var WHEEL_LOCK_TIME = 240;
  var SETTLE_TIME = 720;
  var POINTER_EASE = 0.18;

  function each(list, callback) {
    Array.prototype.forEach.call(list, callback);
  }

  function now() {
    return window.performance && window.performance.now ? window.performance.now() : Date.now();
  }

  function requestFrame(callback) {
    var raf = window.requestAnimationFrame || function (fn) { return window.setTimeout(fn, 16); };
    return raf(callback);
  }

  function cancelFrame(frame) {
    var cancel = window.cancelAnimationFrame || window.clearTimeout;
    if (frame !== null) cancel(frame);
  }

  function closest(element, selector) {
    if (!element || !element.closest) return null;
    return element.closest(selector);
  }

  function init(root) {
    if (!root || root.__prismOrbitDestroy) return;

    var scene = root.querySelector('[data-prism-orbit-scene]');
    var track = root.querySelector('[data-prism-orbit-track]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-prism-orbit-card]'));
    var status = root.querySelector('[data-prism-orbit-status]');
    var previous = root.querySelector('[data-prism-orbit-prev]');
    var next = root.querySelector('[data-prism-orbit-next]');
    var total = cards.length;

    if (!scene || !track || !total || !previous || !next) return;

    var state = {
      activeIndex: 0,
      rotationIndex: 0,
      angleStep: 360 / total,
      pointerId: null,
      pointerStartX: 0,
      pointerStartY: 0,
      pointerLastX: 0,
      pointerDelta: 0,
      pointerStartedAt: 0,
      pointerAxis: null,
      pointerMode: null,
      pointerStartAngle: 0,
      pointerAngleStep: 0,
      pointerMoved: false,
      pointerFrame: null,
      wheelLocked: false,
      suppressClick: false,
      suppressTimer: null,
      settleTimer: null,
      angleTimers: [],
      resizeFrame: null,
      isVisible: true,
      isReducedMotion: false,
      isDestroyed: false,
      stageWidth: 0,
      stageHeight: 0,
      lastAnnouncement: ''
    };

    var cleanups = [];
    var mediaQuery = null;
    var resizeObserver = null;
    var intersectionObserver = null;
    var imageSets = [];

    root.__prismOrbitDestroy = destroy;
    root.dataset.prismOrbitReady = 'true';
    root.dataset.prismOrbitActive = '0';

    function listen(target, eventName, handler, options) {
      if (!target || !target.addEventListener) return;
      target.addEventListener(eventName, handler, options || false);
      cleanups.push(function () {
        target.removeEventListener(eventName, handler, options || false);
      });
    }

    function wrapIndex(index) {
      return (index + total) % total;
    }

    function clamp(value, minimum, maximum) {
      return Math.min(maximum, Math.max(minimum, value));
    }

    function wrapValue(value, length) {
      if (!length) return 0;
      return (value + length) % length;
    }

    function imagesForCard(index) {
      return imageSets[index] || [];
    }

    function angleCount(index) {
      return imagesForCard(index).length;
    }

    function clearAngleTimer(index) {
      if (!state.angleTimers[index]) return;
      window.clearTimeout(state.angleTimers[index]);
      state.angleTimers[index] = null;
    }

    function setAngle(index, requestedAngle, animate) {
      var images = imagesForCard(index);
      var card = cards[index];
      if (!card || !images.length) return;

      var nextAngle = wrapValue(requestedAngle, images.length);
      state.angleIndexes[index] = nextAngle;
      var deck = card.querySelector('[data-prism-orbit-image-deck]');
      if (deck) {
        clearAngleTimer(index);
        deck.classList.toggle('is-angle-changing', Boolean(animate));
        if (animate && !state.isReducedMotion) {
          state.angleTimers[index] = window.setTimeout(function () {
            deck.classList.remove('is-angle-changing');
            state.angleTimers[index] = null;
          }, 380);
        }
      }

      each(images, function (image, imageIndex) {
        var active = imageIndex === nextAngle;
        image.classList.toggle('is-angle-active', active);
        image.setAttribute('data-prism-orbit-angle-state', active ? 'active' : 'inactive');
        image.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    function setupImageSets() {
      imageSets = cards.map(function (card) {
        return Array.prototype.slice.call(card.querySelectorAll('img.prism-orbit__product-image'));
      });

      state.angleIndexes = imageSets.map(function () { return 0; });
      each(imageSets, function (images, cardIndex) {
        setAngle(cardIndex, 0, false);
        cards[cardIndex].setAttribute('data-prism-orbit-image-count', String(images.length));
      });
    }

    function preloadCardImages(cardIndex) {
      each(imagesForCard(cardIndex), function (image) {
        if (!image || image.complete || image.loading === 'eager' || !image.src || image.dataset.prismOrbitPreloadStarted === 'true') return;
        image.dataset.prismOrbitPreloadStarted = 'true';
        var preload = new Image();
        preload.decoding = 'async';
        preload.src = image.currentSrc || image.src;
      });
    }

    function readTitle(index) {
      var card = cards[index];
      return card ? card.getAttribute('data-product-name') || 'product' : 'product';
    }

    function announce(index) {
      var message = 'Product ' + (index + 1) + ' de ' + total + ': ' + readTitle(index) + '.';
      if (!status || message === state.lastAnnouncement) return;
      state.lastAnnouncement = message;
      status.textContent = message;
    }

    function setSceneState(className, enabled) {
      scene.classList.toggle(className, enabled);
      root.classList.toggle(className, enabled);
    }

    function setCardState(card, index) {
      var isActive = index === state.activeIndex;
      card.classList.toggle('is-active', isActive);
      card.classList.toggle('is-inactive', !isActive);
      card.setAttribute('data-prism-orbit-state', isActive ? 'active' : 'inactive');
      card.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      var link = card.querySelector('a');
      if (link) link.tabIndex = isActive ? 0 : -1;
    }

    function updateCards() {
      each(cards, setCardState);
      root.dataset.prismOrbitActive = String(state.activeIndex);
      announce(state.activeIndex);
    }

    function applyTrackAngle(angle) {
      if (state.isDestroyed) return;
      track.style.transform = 'translate3d(-50%, -50%, 0) rotateY(' + angle + 'deg)';
      state.renderedAngle = angle;
    }

    function targetAngle() {
      return state.rotationIndex * -state.angleStep;
    }

    function shortestDelta(targetIndex) {
      var delta = targetIndex - state.activeIndex;
      if (Math.abs(delta) > total / 2) delta += delta > 0 ? -total : total;
      return delta;
    }

    function markSettling() {
      setSceneState('is-settling', true);
      if (state.settleTimer) window.clearTimeout(state.settleTimer);
      state.settleTimer = window.setTimeout(function () {
        state.settleTimer = null;
        setSceneState('is-settling', false);
      }, state.isReducedMotion ? 0 : SETTLE_TIME);
    }

    function commit(index, explicitDelta, source) {
      if (state.isDestroyed) return;

      var targetIndex = wrapIndex(index);
      var delta = typeof explicitDelta === 'number' ? explicitDelta : shortestDelta(targetIndex);
      state.activeIndex = targetIndex;
      state.rotationIndex += delta;
      root.dataset.prismOrbitSource = source || 'control';
      setAngle(targetIndex, state.angleIndexes[targetIndex] || 0, false);
      preloadCardImages(targetIndex);
      track.style.transition = state.isReducedMotion ? 'none' : '';
      applyTrackAngle(targetAngle());
      updateCards();
      markSettling();
    }

    function moveBy(direction, source) {
      if (!direction) return;
      commit(state.activeIndex + direction, direction, source || 'control');
    }

    function settleToCurrent() {
      track.style.transition = state.isReducedMotion ? 'none' : '';
      applyTrackAngle(targetAngle());
      markSettling();
    }

    function renderDrag() {
      if (state.pointerId === null || state.isDestroyed) return;
      if (state.pointerMode === 'angle') return;
      var angle = targetAngle() + (state.pointerDelta * POINTER_EASE);
      track.style.transition = 'none';
      setSceneState('is-dragging', true);
      applyTrackAngle(angle);
    }

    function queueDragRender() {
      if (state.pointerFrame !== null) return;
      state.pointerFrame = requestFrame(function () {
        state.pointerFrame = null;
        renderDrag();
      });
    }

    function renderAngleDrag() {
      if (state.pointerId === null || state.pointerMode !== 'angle') return;
      var cardIndex = state.activeIndex;
      var count = angleCount(cardIndex);
      if (count < 2) return;

      var pixelsPerAngle = Math.max(42, (state.stageWidth || scene.clientWidth || 640) / count);
      var angleDelta = Math.round(-state.pointerDelta / pixelsPerAngle);
      var requestedAngle = state.pointerStartAngle + angleDelta;
      if (wrapValue(requestedAngle, count) !== state.angleIndexes[cardIndex]) {
        setAngle(cardIndex, requestedAngle, true);
      }
      setSceneState('is-angle-dragging', true);
      preloadCardImages(cardIndex);
    }

    function releasePointer(pointerId) {
      if (pointerId === null || !scene.releasePointerCapture) return;
      try { scene.releasePointerCapture(pointerId); } catch (error) { /* capture may already be released */ }
    }

    function suppressNextClick() {
      state.suppressClick = true;
      if (state.suppressTimer) window.clearTimeout(state.suppressTimer);
      state.suppressTimer = window.setTimeout(function () {
        state.suppressClick = false;
        state.suppressTimer = null;
      }, 500);
    }

    function finishPointer(cancelled) {
      if (state.pointerId === null) return;

      var delta = state.pointerDelta;
      var duration = Math.max(1, now() - state.pointerStartedAt);
      var velocity = delta / duration;
      var moved = state.pointerMoved;
      var axis = state.pointerAxis;
      var mode = state.pointerMode;
      var pointerId = state.pointerId;

      releasePointer(pointerId);
      state.pointerId = null;
      state.pointerDelta = 0;
      state.pointerAxis = null;
      state.pointerMode = null;
      state.pointerMoved = false;
      setSceneState('is-dragging', false);
      setSceneState('is-angle-dragging', false);

      if (state.pointerFrame !== null) {
        cancelFrame(state.pointerFrame);
        state.pointerFrame = null;
      }

      if (moved && axis === 'x') suppressNextClick();
      if (mode === 'angle') {
        settleToCurrent();
        return;
      }
      if (cancelled || axis === 'y') {
        settleToCurrent();
        return;
      }

      var shouldAdvance = Math.abs(delta) >= SNAP_DISTANCE || Math.abs(velocity) >= SNAP_VELOCITY;
      var direction = delta < 0 ? 1 : -1;
      commit(state.activeIndex + (shouldAdvance ? direction : 0), shouldAdvance ? direction : 0, 'drag');
    }

    function onPointerDown(event) {
      if (state.pointerId !== null || event.button !== 0 || event.isPrimary === false) return;
      if (closest(event.target, 'button')) return;

      state.pointerId = event.pointerId;
      state.pointerStartX = event.clientX;
      state.pointerStartY = event.clientY;
      state.pointerLastX = event.clientX;
      state.pointerDelta = 0;
      state.pointerStartedAt = now();
      state.pointerAxis = null;
      var originCard = closest(event.target, '[data-prism-orbit-card]');
      var activeCard = cards[state.activeIndex];
      var startsOnActiveProduct = originCard === activeCard && Boolean(closest(event.target, 'a'));
      state.pointerMode = startsOnActiveProduct && angleCount(state.activeIndex) > 1 ? 'angle' : 'product';
      state.pointerStartAngle = state.angleIndexes[state.activeIndex] || 0;
      state.pointerMoved = false;

      if (scene.setPointerCapture) {
        try { scene.setPointerCapture(event.pointerId); } catch (error) { /* unsupported capture */ }
      }
    }

    function onPointerMove(event) {
      if (state.pointerId !== event.pointerId || state.isDestroyed) return;

      var deltaX = event.clientX - state.pointerStartX;
      var deltaY = event.clientY - state.pointerStartY;
      var distance = Math.max(Math.abs(deltaX), Math.abs(deltaY));

      if (!state.pointerAxis && distance >= 6) {
        state.pointerAxis = Math.abs(deltaX) >= Math.abs(deltaY) ? 'x' : 'y';
      }

      if (state.pointerAxis !== 'x') {
        if (state.pointerAxis === 'y') finishPointer(false);
        return;
      }

      if (Math.abs(deltaX) >= DRAG_THRESHOLD) state.pointerMoved = true;
      state.pointerDelta = deltaX;
      state.pointerLastX = event.clientX;
      event.preventDefault();
      if (state.pointerMode === 'angle') {
        renderAngleDrag();
        return;
      }
      queueDragRender();
    }

    function onPointerUp() {
      finishPointer(false);
    }

    function onPointerCancel() {
      finishPointer(true);
    }

    function onClick(event) {
      if (!state.suppressClick) return;
      if (closest(event.target, 'a')) {
        event.preventDefault();
        event.stopPropagation();
      }
      state.suppressClick = false;
      if (state.suppressTimer) {
        window.clearTimeout(state.suppressTimer);
        state.suppressTimer = null;
      }
    }

    function onWheel(event) {
      var delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
      if (!delta || state.wheelLocked || state.pointerId !== null) return;

      event.preventDefault();
      state.wheelLocked = true;
      moveBy(delta > 0 ? 1 : -1, 'wheel');
      window.setTimeout(function () { state.wheelLocked = false; }, WHEEL_LOCK_TIME);
    }

    function onKeyDown(event) {
      var direction = 0;
      if (event.key === 'ArrowLeft') direction = -1;
      if (event.key === 'ArrowRight') direction = 1;

      if (direction) {
        event.preventDefault();
        moveBy(direction, 'keyboard');
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        commit(0, shortestDelta(0), 'keyboard');
      }

      if (event.key === 'End') {
        event.preventDefault();
        commit(total - 1, shortestDelta(total - 1), 'keyboard');
      }
    }

    function onFocus() {
      setSceneState('is-focused', true);
    }

    function onBlur() {
      setSceneState('is-focused', false);
    }

    function measure() {
      state.stageWidth = scene.clientWidth || 0;
      state.stageHeight = scene.clientHeight || 0;
      root.style.setProperty('--prism-orbit-stage-width', state.stageWidth + 'px');
      root.style.setProperty('--prism-orbit-stage-height', state.stageHeight + 'px');
      root.dataset.prismOrbitSize = state.stageWidth > 749 ? 'wide' : 'compact';
    }

    function requestMeasure() {
      if (state.resizeFrame !== null) return;
      state.resizeFrame = requestFrame(function () {
        state.resizeFrame = null;
        measure();
      });
    }

    function onImageLoad(event) {
      var image = event.target;
      if (!image || !image.classList) return;
      image.classList.add('is-loaded');
      var card = closest(image, '[data-prism-orbit-card]');
      if (card) card.setAttribute('data-prism-orbit-image-state', 'loaded');
      requestMeasure();
    }

    function markAlreadyLoaded() {
      each(cards, function (card) {
        var image = card.querySelector('img');
        if (!image) return;
        if (image.complete && image.naturalWidth > 0) onImageLoad({ target: image });
      });
    }

    function preloadImages() {
      each(imageSets, function (images) {
        each(images, function (image) {
          if (!image || image.complete || image.loading === 'eager' || !image.currentSrc && !image.src) return;
          var preload = new Image();
          preload.decoding = 'async';
          preload.src = image.currentSrc || image.src;
        });
      });
    }

    function setReducedMotion(value) {
      state.isReducedMotion = Boolean(value);
      root.dataset.prismOrbitMotion = state.isReducedMotion ? 'reduced' : 'full';
      if (state.isReducedMotion) {
        track.style.transition = 'none';
        applyTrackAngle(targetAngle());
      }
    }

    function onMotionChange(event) {
      setReducedMotion(event.matches);
    }

    function onVisibilityChange(entries) {
      if (!entries || !entries.length) return;
      state.isVisible = entries[0].isIntersecting;
      root.dataset.prismOrbitVisible = state.isVisible ? 'true' : 'false';
    }

    function bindMotionPreference() {
      if (!window.matchMedia) return;
      mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReducedMotion(mediaQuery.matches);
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', onMotionChange);
        cleanups.push(function () { mediaQuery.removeEventListener('change', onMotionChange); });
      } else if (mediaQuery.addListener) {
        mediaQuery.addListener(onMotionChange);
        cleanups.push(function () { mediaQuery.removeListener(onMotionChange); });
      }
    }

    function bindObservers() {
      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(requestMeasure);
        resizeObserver.observe(scene);
        cleanups.push(function () { resizeObserver.disconnect(); });
      } else {
        listen(window, 'resize', requestMeasure);
      }

      if (window.IntersectionObserver) {
        intersectionObserver = new IntersectionObserver(onVisibilityChange, { threshold: 0.05 });
        intersectionObserver.observe(root);
        cleanups.push(function () { intersectionObserver.disconnect(); });
      }
    }

    function bindImages() {
      each(imageSets, function (images) {
        each(images, function (image) {
          listen(image, 'load', onImageLoad);
        });
      });
      markAlreadyLoaded();
      if (window.requestIdleCallback) {
        window.requestIdleCallback(preloadImages, { timeout: 1200 });
      } else {
        window.setTimeout(preloadImages, 120);
      }
    }

    function destroy() {
      if (state.isDestroyed) return;
      state.isDestroyed = true;
      if (state.pointerFrame !== null) cancelFrame(state.pointerFrame);
      if (state.resizeFrame !== null) cancelFrame(state.resizeFrame);
      if (state.suppressTimer) window.clearTimeout(state.suppressTimer);
      if (state.settleTimer) window.clearTimeout(state.settleTimer);
      each(state.angleTimers, function (timer) {
        if (timer) window.clearTimeout(timer);
      });
      each(cleanups, function (cleanup) { cleanup(); });
      root.__prismOrbitDestroy = null;
      root.dataset.prismOrbitReady = 'false';
    }

    setupImageSets();

    cards.forEach(function (card, index) {
      card.style.setProperty('--card-angle', (index * state.angleStep) + 'deg');
      card.setAttribute('data-prism-orbit-state', index === 0 ? 'active' : 'inactive');
    });

    listen(previous, 'click', function () { moveBy(-1, 'control'); });
    listen(next, 'click', function () { moveBy(1, 'control'); });
    listen(scene, 'pointerdown', onPointerDown);
    listen(scene, 'pointermove', onPointerMove, { passive: false });
    listen(scene, 'pointerup', onPointerUp);
    listen(scene, 'pointercancel', onPointerCancel);
    listen(scene, 'lostpointercapture', onPointerCancel);
    listen(scene, 'click', onClick, true);
    listen(scene, 'wheel', onWheel, { passive: false });
    listen(scene, 'keydown', onKeyDown);
    listen(scene, 'focusin', onFocus);
    listen(scene, 'focusout', onBlur);
    bindMotionPreference();
    bindObservers();
    bindImages();
    measure();
    updateCards();
    applyTrackAngle(targetAngle());
  }

  function initAll() {
    each(document.querySelectorAll(ROOT_SELECTOR), init);
  }

  function findRoot(target) {
    if (!target) return null;
    if (target.matches && target.matches(ROOT_SELECTOR)) return target;
    return target.querySelector ? target.querySelector(ROOT_SELECTOR) : null;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(findRoot(event.target));
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = findRoot(event.target);
    if (root && root.__prismOrbitDestroy) root.__prismOrbitDestroy();
  });
}());
